import { baseURL } from "../../constants/endPoints";
import * as apiService from "./apiService";

// baseURL has carried a trailing newline in at least one deployed env var,
// which the browser strips from the URL and leaves a double slash that misses
// the /api mount. Trim whitespace as well as slashes.
const trimBase = (url) => (url || "").trim().replace(/\/+$/, "");

// The render behind this endpoint is an uncached headless-Chromium PDF build
// shared across a small render pool. Cut the client off well before an agent
// gives up and clicks again, so the failure path (a distinct error, not a
// silent hang) triggers before that duplicate click does.
const DOWNLOAD_TIMEOUT_MS = 60000;

export const isPdfResponse = (contentType) =>
  String(contentType || "").toLowerCase().includes("pdf");

export const extractPdfFilename = (disposition, fallback) => {
  const match = String(disposition || "").match(/filename="?([^";]+)"?/i);
  return match ? match[1] : fallback;
};

// apiCallBlob requests responseType: "blob", which applies to error bodies
// too, so the API's JSON error message arrives as a Blob instead of parsed
// data. Recover it so the agent sees the backend's actual explanation rather
// than a generic fallback whenever one is available.
const extractApiMessage = async (axiosError) => {
  const data = axiosError?.response?.data;
  if (!data || typeof data.text !== "function") return null;
  try {
    const parsed = JSON.parse(await data.text());
    return typeof parsed?.message === "string" ? parsed.message : null;
  } catch {
    return null;
  }
};

const toReportError = async (axiosError) => {
  const err = new Error("Report unavailable");
  err.status = axiosError?.response?.status;
  err.timedOut = axiosError?.code === "ECONNABORTED";
  err.apiMessage = await extractApiMessage(axiosError);
  return err;
};

const performDownload = async (inventoryId) => {
  const url = `${trimBase(baseURL)}/api/v2/active-buyer-report/${encodeURIComponent(
    inventoryId
  )}`;

  let response;
  try {
    response = await apiService.apiCallBlob(url, "GET", DOWNLOAD_TIMEOUT_MS);
  } catch (axiosError) {
    throw await toReportError(axiosError);
  }

  const contentType = response?.headers?.["content-type"] || "";
  if (!isPdfResponse(contentType)) {
    const err = new Error("Report unavailable");
    err.status = response?.status;
    throw err;
  }

  const filename = extractPdfFilename(
    response?.headers?.["content-disposition"] || "",
    `buyer-match-report-${inventoryId}.pdf`
  );

  const objectUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking synchronously right after click() is a known race in
  // Firefox/Safari: the download can be cancelled before it starts because
  // the object URL goes invalid while the browser is still opening it.
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);
};

// Keyed by inventoryId so a second click on the same property while its
// download is still in flight reuses the pending request instead of
// spawning another render. Different properties still download in parallel.
const pendingDownloads = new Map();

/**
 * Fetch the Buyer Match Report for one property and save it.
 *
 * The report is a PDF and nothing else, so there is no page to route to and
 * nothing to render on the way. A 422 means the API declined to build one for
 * lack of demand; the caller decides what to say about that.
 */
export const downloadBuyerMatchReport = (inventoryId) => {
  if (pendingDownloads.has(inventoryId)) {
    return pendingDownloads.get(inventoryId);
  }

  const promise = performDownload(inventoryId).finally(() => {
    pendingDownloads.delete(inventoryId);
  });
  pendingDownloads.set(inventoryId, promise);
  return promise;
};

const STATUS_FALLBACK_MESSAGE = {
  422: "Not enough buyer demand data yet to generate this report.",
  503: "Report generation is busy right now. Please try again in a few seconds.",
};

export const buyerMatchReportErrorMessage = (err) => {
  if (err?.timedOut) {
    return "This is taking longer than expected. Please try again in a moment.";
  }
  return (
    err?.apiMessage ||
    STATUS_FALLBACK_MESSAGE[err?.status] ||
    "Couldn't download the report. Please try again."
  );
};
